import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Badge, getStatusBadgeVariant, ContentLoader, Pagination, StatsCard, StatsGrid, BackButton } from '@/components/common';
import { EntityLink, PolicyNumber } from '@/components/common';
import {
  usePOSP,
  usePOSPPayouts,
  usePOSPPolicies,
  usePOSPEarningsSummary,
} from '@/hooks/useBrokers';
import { useAchievement } from '@/hooks/useTargets';
import { formatDate, formatCurrency, titleCase } from '@/utils';
import { PeriodToggle, type ProfilePeriod } from './PeriodToggle';

// ── Reports (self-service /portal/:id) ───────────────────
//
// P2: this screen used to be "My Portal" and carried the agent's whole profile
// alongside their numbers. The profile half -- Activation Progress, Required
// Documents, Contact Information, Status, Bank Details and BOTH certification
// cards -- now lives on /settings > Profile (`PospProfileSection`). What is left is
// what a report is: the stats, the policies they sold, their payouts, their
// targets and the shortcuts.
//
// The URL is still /portal; only the label changed, so no bookmark breaks.

export function POSPDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [policiesPage, setPoliciesPage] = useState(1);
  // Item 14: default the holistic profile to the current financial year, with
  // an All-time toggle — the same window /policies/ and /earnings_summary/
  // both already support via ?period.
  const [period, setPeriod] = useState<ProfilePeriod>('fy');

  const { data: posp, isLoading, error } = usePOSP(id!);
  /**
   * P3 -- the Performance Targets card.
   *
   * It used to read `GET /brokers/posps/{id}/targets/`, a route that DOES NOT
   * EXIST: `POSPViewSet` declares no `targets` action and `brokers/urls.py`
   * registers only the flat legacy `/brokers/targets/`. Every request 404'd,
   * react-query reported an error nobody rendered, and the card fell through to
   * "No targets set." for every agent -- including the ones whose broker admin
   * had set a number for them. Classic shape for this codebase: a screen that
   * reports an absence it never actually asked about.
   *
   * `/targets/achievement/` is the endpoint that was always right for this. It
   * already permits posp/super_posp, already self-scopes to the caller's own
   * POSP row (`achievement_rows`, apps/targets/services.py), and returns the
   * TARGET and the ACHIEVEMENT in one call. No parameters: the server ignores
   * ?posp= for a POSP caller by design, and its default window is the last six
   * months ending with the current one.
   *
   * Because the server self-scopes, this answers for the LOGGED-IN agent. A
   * super-POSP opening /portal/<team-member> would otherwise be shown their own
   * numbers under someone else's name, so the row is matched on `entity_id`
   * and anything else is reported as not available rather than mislabelled.
   */
  const {
    data: achievement,
    isLoading: targetsLoading,
    isError: targetsFailed,
  } = useAchievement();
  const targetRow = achievement?.rows.find((row) => row.entity_id === id);
  const { data: payouts } = usePOSPPayouts(id!);
  const { data: policiesData, isLoading: policiesLoading } = usePOSPPolicies(id!, policiesPage, { period });
  const { data: earningsSummary, isLoading: earningsLoading } = usePOSPEarningsSummary(id!, { period });

  const handlePeriodChange = (next: ProfilePeriod) => {
    setPeriod(next);
    setPoliciesPage(1);
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !posp) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">POSP not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The POSP you are looking for does not exist.</p>
        <Link to="/portal" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Reports
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/portal" />
      </div>
      <PageHeader
        title={posp.user_name}
        subtitle={posp.posp_code}
        breadcrumbs={[
          { label: 'Reports', href: '/portal' },
          { label: posp.user_name },
        ]}
      />

      {/* Account Overview */}
      <div className="mb-6">
        <StatsGrid columns={3}>
          <StatsCard
            title="Total Policies"
            value={policiesData?.count ?? 0}
            loading={policiesLoading}
            subtitle="Policies issued via this POSP"
          />
          <StatsCard
            title={period === 'fy' ? 'Commission (This FY)' : 'Commission (All-time)'}
            value={
              earningsSummary
                ? formatCurrency(
                    earningsSummary.your_commission ?? earningsSummary.paid_to_posp ?? '0',
                  )
                : '—'
            }
            loading={earningsLoading}
            subtitle={
              period === 'fy'
                ? 'Your commission this financial year (Apr–Mar)'
                : 'Your commission across all policies'
            }
          />
          <StatsCard
            title="Payouts"
            value={payouts?.length ?? 0}
            subtitle="Recorded payout periods"
          />
        </StatsGrid>
      </div>

      {/* Policies Table */}
      <div className="mb-6">
        <Card padding="none">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Policies</h3>
            <PeriodToggle period={period} onChange={handlePeriodChange} />
          </div>
          {policiesLoading ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading policies…</div>
          ) : policiesData && policiesData.results.length > 0 ? (() => {
            const hasCommission = policiesData.results.some((p) => p.commission);
            return (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Policy #</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Holder</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Premium</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                      {hasCommission && (
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Commission</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {policiesData.results.map((policy) => {
                      const commValue =
                        policy.commission?.your_commission ??
                        policy.commission?.paid_to_posp ??
                        null;
                      return (
                        <tr key={policy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-4 py-3 text-sm">
                            {/* B-06: the insurer's number, not ours (ours is tagged when it is all there is). */}
                            <PolicyNumber
                              source={policy}
                              render={(text) => <EntityLink kind="policy" id={policy.id} name={text} />}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {policy.customer ? (
                              <EntityLink kind="contact" id={policy.customer} name={policy.holder_name} />
                            ) : (
                              policy.holder_name
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {formatCurrency(policy.total_premium)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={getStatusBadgeVariant(policy.status)} size="sm">
                              {titleCase(policy.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {policy.policy_date
                              ? formatDate(policy.policy_date)
                              : policy.inception_date
                                ? formatDate(policy.inception_date)
                                : '—'}
                          </td>
                          {hasCommission && (
                            <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">
                              {commValue != null ? formatCurrency(commValue) : <span className="text-gray-400 dark:text-gray-500">—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Math.ceil((policiesData.count ?? 0) / 20) > 1 && (
                <Pagination
                  currentPage={policiesPage}
                  totalPages={Math.ceil((policiesData.count ?? 0) / 20)}
                  totalItems={policiesData.count ?? 0}
                  itemsPerPage={20}
                  onPageChange={setPoliciesPage}
                />
              )}
            </>
            );
          })() : (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">No policies found for this POSP.</div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Targets — see the note on `useAchievement` above. */}
          <Card>
            <CardHeader
              title="Performance Targets"
              subtitle={
                achievement ? `${achievement.from} to ${achievement.to}` : undefined
              }
              action={
                <Link to="/portal/targets" className="text-sm text-primary-600 hover:underline">
                  Targets and incentives
                </Link>
              }
            />
            {targetsLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading targets…</p>
            ) : targetsFailed ? (
              // Four states, not two. A failed request used to render as "No
              // targets set." — an authoritative claim about data nobody read.
              <p className="text-sm text-red-600 dark:text-red-400">
                Your targets could not be loaded. Please try again shortly.
              </p>
            ) : !targetRow ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Targets are shown on the agent’s own Reports page.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Total for the period
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {targetRow.percentage != null
                        ? `${targetRow.percentage.toFixed(1)}% achieved`
                        : 'No target set'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Target Premium</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(targetRow.target)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Achieved Premium</p>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">
                        {formatCurrency(targetRow.achieved)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-600 rounded-full"
                        style={{ width: `${Math.min(targetRow.percentage ?? 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* The per-month series behind that total. `percentage` is null
                    — not 0 — for a month with no target, so "unknown" is drawn
                    as a dash rather than as a missed target. */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Month</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Target</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Achieved</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {targetRow.series.map((point) => (
                        <tr key={point.period}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{point.period}</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-gray-100">
                            {formatCurrency(point.target)}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-gray-100">
                            {formatCurrency(point.achieved)}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-500 dark:text-gray-400">
                            {point.percentage != null ? `${point.percentage.toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {Number(targetRow.target) <= 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Your broker has set no monthly target for this period. The achieved
                    premium above is still your own.
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Payouts */}
          <Card>
            <CardHeader title="Payouts" />
            {payouts && payouts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Period</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Commission</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Deductions</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Net Payout</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {payouts.map((payout) => (
                      <tr key={payout.id}>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {payout.month} {payout.year}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(payout.total_commission)}
                        </td>
                        <td className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
                          -{formatCurrency(payout.deductions)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(payout.net_payout)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusBadgeVariant(payout.status)} size="sm">
                            {titleCase(payout.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No payout records.</p>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              <Link to={`/policies?posp=${id}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  My Policy
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
